import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import { getCompletionMatrix, listOverdue, type MatrixStatus } from '@/api/trainingAdmin.api';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Tabs } from '@/components/ui/Tabs';
import { describeTrainingError } from '@/lib/training/errors';
import { TrainingNoAccessCard } from './_chrome';
import { useTrainingAdminGate } from './_gate';

/**
 * Training reports (view_training_reports / superadmin) — the
 * day-to-day operational view: members × courses completion matrix and
 * the overdue list. Record-grade retrieval lives on the compliance
 * training-records surface, not here.
 */

const STATUS_META: Record<MatrixStatus, { label: string; variant: BadgeProps['variant'] }> = {
  completed: { label: 'Completed', variant: 'success' },
  in_progress: { label: 'In progress', variant: 'info' },
  not_started: { label: 'Not started', variant: 'neutral' },
  overdue: { label: 'Overdue', variant: 'danger' },
  expired: { label: 'Expired', variant: 'warning' },
  not_enrolled: { label: '—', variant: 'neutral' },
};

export function TrainingReportsPage() {
  const { canViewReports } = useTrainingAdminGate();
  const [tab, setTab] = useState('matrix');

  if (!canViewReports) {
    return <TrainingNoAccessCard capability="view_training_reports" />;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="typo-h1">Training reports</h1>
        <p className="mt-1 text-[14px] text-kw-muted">Who has completed what — and who is behind.</p>
      </div>

      <Tabs
        tabs={[
          { id: 'matrix', label: 'Completion matrix' },
          { id: 'overdue', label: 'Overdue' },
        ]}
        activeTab={tab}
        onChange={setTab}
      />

      {tab === 'matrix' ? <MatrixTab /> : <OverdueTab />}
    </div>
  );
}

function MatrixTab() {
  const matrixQuery = useQuery({
    queryKey: ['training-admin', 'completion-matrix'],
    queryFn: () => getCompletionMatrix(),
  });

  const cellByKey = useMemo(() => {
    const map = new Map<string, MatrixStatus>();
    for (const cell of matrixQuery.data?.cells ?? []) {
      map.set(`${cell.courseId}:${cell.membershipId}`, cell.status);
    }
    return map;
  }, [matrixQuery.data]);

  if (matrixQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (matrixQuery.error || !matrixQuery.data) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <p className="text-[13px] text-kw-danger-text">
          {describeTrainingError(matrixQuery.error, 'Could not load the completion matrix.')}
        </p>
      </Card>
    );
  }

  const { courses, members } = matrixQuery.data;

  if (courses.length === 0) {
    return (
      <Card className="p-8 text-center text-[13px] text-kw-muted">
        No published courses yet — the matrix fills in once courses are published and assigned.
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2">
        {(['completed', 'in_progress', 'not_started', 'overdue', 'expired'] as MatrixStatus[]).map(
          (status) => (
            <span key={status} className="flex items-center gap-1 text-[12px] text-kw-muted">
              <Badge variant={STATUS_META[status].variant}>{STATUS_META[status].label}</Badge>
            </span>
          ),
        )}
        <span className="text-[12px] text-kw-muted">— = not enrolled</span>
      </div>

      <Card className="overflow-x-auto p-0">
        <table className="min-w-full text-left text-[13px]">
          <thead>
            <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
              <th className="sticky left-0 bg-white px-4 py-2.5 font-medium">Member</th>
              {courses.map((c) => (
                <th key={c.id} className="px-4 py-2.5 font-medium">
                  {c.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-kw-border">
            {members.map((m) => (
              <tr key={m.membershipId}>
                <td className="sticky left-0 bg-white px-4 py-2.5">
                  <p className="font-medium text-kw-shark">{m.name}</p>
                  <p className="text-[11px] text-kw-muted">{m.role.replace(/_/g, ' ')}</p>
                </td>
                {courses.map((c) => {
                  const status = cellByKey.get(`${c.id}:${m.membershipId}`) ?? 'not_enrolled';
                  const meta = STATUS_META[status];
                  return (
                    <td key={c.id} className="px-4 py-2.5">
                      {status === 'not_enrolled' ? (
                        <span className="text-kw-ghost">—</span>
                      ) : (
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function OverdueTab() {
  const overdueQuery = useQuery({
    queryKey: ['training-admin', 'overdue'],
    queryFn: listOverdue,
  });

  if (overdueQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (overdueQuery.error) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <p className="text-[13px] text-kw-danger-text">
          {describeTrainingError(overdueQuery.error, 'Could not load the overdue list.')}
        </p>
      </Card>
    );
  }

  const rows = overdueQuery.data ?? [];

  if (rows.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-3 p-12 text-center">
        <CheckCircle2 className="size-10 text-kw-tag-text" />
        <p className="text-[14px] font-medium text-kw-shark">Nobody is overdue</p>
      </Card>
    );
  }

  return (
    <Card className="overflow-x-auto p-0">
      <table className="min-w-full text-left text-[13px]">
        <thead>
          <tr className="border-b border-kw-border text-[12px] uppercase tracking-wide text-kw-muted">
            <th className="px-4 py-2.5 font-medium">Trainee</th>
            <th className="px-4 py-2.5 font-medium">Course</th>
            <th className="px-4 py-2.5 font-medium">Due</th>
            <th className="px-4 py-2.5 font-medium">Days overdue</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-kw-border">
          {rows.map((row) => (
            <tr key={row.enrollmentId}>
              <td className="px-4 py-3">
                <p className="font-medium text-kw-shark">{row.traineeName}</p>
                <p className="text-[11px] text-kw-muted">{row.traineeEmail}</p>
              </td>
              <td className="px-4 py-3 text-kw-shark">{row.course.title}</td>
              <td className="px-4 py-3 text-kw-shark">
                {row.dueAt ? new Date(row.dueAt).toLocaleDateString() : '—'}
              </td>
              <td className="px-4 py-3">
                <Badge variant="danger">
                  {row.daysOverdue} day{row.daysOverdue === 1 ? '' : 's'}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
